import React from 'react';
import { Edit2, Trash2, Heart, ChevronUp, ChevronDown } from 'lucide-react';
import { GENDER_COLORS } from './familyTreeHelpers';
import { RootBadge } from './RootBadge';

export function PersonCard({ node, onEdit, onDelete, onAddChild, onAddParent, onAddPartner, onMakeRoot, nodeMap, isOwner }) {
  const gender = node.gender || 'other';
  const colors = GENDER_COLORS[gender];
  const hasPartner = !!node.partner_id && !!nodeMap[node.partner_id];
  const initial = (node.name || '?')[0].toUpperCase();
  const parentCount = (node.parent_ids || []).filter(id => nodeMap[id]).length;

  return (
    <div
      className="relative flex flex-col items-center gap-2"
      data-testid={`tree-node-${node.node_id}`}
    >
      {/* Add parent button (above card) */}
      {isOwner && parentCount < 2 && (
        <button
          onClick={() => onAddParent(node)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-medium transition-colors"
          data-testid={`add-parent-${node.node_id}`}
        >
          <ChevronUp className="w-3 h-3" />
          <span>Add Parent</span>
        </button>
      )}

      {/* Card */}
      <div className={`relative w-40 sm:w-44 px-3 py-3 rounded-xl border-2 ${colors.border} ${colors.bg} shadow-md hover:shadow-lg transition-all duration-200`}>
        {/* Edit/Delete buttons - always visible */}
        {isOwner && (
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(node); }}
              className="w-6 h-6 rounded-md bg-white/90 hover:bg-blue-100 border border-gray-200 flex items-center justify-center"
              title="Edit"
              data-testid={`edit-node-${node.node_id}`}
            >
              <Edit2 className="w-3 h-3 text-blue-600" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(node); }}
              className="w-6 h-6 rounded-md bg-white/90 hover:bg-red-100 border border-gray-200 flex items-center justify-center"
              title="Remove"
              data-testid={`delete-node-${node.node_id}`}
            >
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </div>
        )}

        {/* Avatar + Name */}
        <div className="flex flex-col items-center gap-1.5">
          <div className={`w-12 h-12 rounded-full ${colors.avatar} flex items-center justify-center text-white font-bold text-lg shadow-inner`}>
            {initial}
          </div>
          <div className="text-center">
            <p className="font-semibold text-gray-900 text-sm leading-tight truncate max-w-[130px]">{node.name}</p>
            {(node.birth_year || node.death_year) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {node.birth_year || '?'}{node.death_year ? ` - ${node.death_year}` : ''}
              </p>
            )}
          </div>
          {node.is_root ? (
            <RootBadge nodeId={node.node_id} />
          ) : isOwner && (
            <button
              type="button"
              onClick={() => onMakeRoot(node)}
              className="text-xs font-medium px-3 py-1.5 min-h-[32px] rounded-md border border-dashed border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
              data-testid={`make-root-${node.node_id}`}
            >
              Make Root
            </button>
          )}
        </div>

        {/* Add Partner button INSIDE card - prominent when no partner */}
        {isOwner && !hasPartner && (
          <button
            onClick={() => onAddPartner(node)}
            className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-100 hover:bg-pink-200 text-pink-700 text-xs font-semibold transition-colors border border-pink-200"
            data-testid={`add-partner-${node.node_id}`}
          >
            <Heart className="w-3 h-3" />
            <span>Add Spouse / Partner</span>
          </button>
        )}
      </div>

      {/* Add child button (below card) */}
      {isOwner && (
        <button
          onClick={() => onAddChild(node)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs font-medium transition-colors"
          data-testid={`add-child-${node.node_id}`}
        >
          <ChevronDown className="w-3 h-3" />
          <span>Add Child</span>
        </button>
      )}
    </div>
  );
}
